import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { getBaseUrl } from '../utils/environment';

const SESSION_STORAGE_KEY = 'gather.admin.session';

let accessToken: string | undefined;
let refreshToken: string | undefined;

export const BASE_URL = getBaseUrl();

export const setAccessToken = (token?: string): void => {
  accessToken = token;
};

export const setRefreshToken = (token?: string): void => {
  refreshToken = token;
};

export const getAccessToken = (): string | undefined => accessToken;

// Hydrate tokens from localStorage so a hard refresh keeps the admin session
// (matches mobile's Storage-backed hydration in src/redux/baseQuery.ts).
try {
  if (typeof localStorage !== 'undefined') {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as {
        accessToken?: string;
        refreshToken?: string;
      };
      if (parsed.accessToken) accessToken = parsed.accessToken;
      if (parsed.refreshToken) refreshToken = parsed.refreshToken;
    }
  }
} catch {
  // ignore: corrupt session is overwritten on next login
}

const prepareHeaders = (headers: Headers): Headers => {
  if (accessToken) {
    headers.set('authorization', `Bearer ${accessToken}`);
  }
  return headers;
};

const query = fetchBaseQuery({
  baseUrl: BASE_URL,
  prepareHeaders,
});

let refreshPromise: Promise<boolean> | null = null;

const attemptTokenRefresh = async (
  api: Parameters<typeof query>[1],
  extraOptions: Parameters<typeof query>[2],
): Promise<boolean> => {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    if (!refreshToken) return false;
    const refreshResult = await query(
      { url: '/auth/refresh', method: 'POST', body: { refreshToken } },
      api,
      extraOptions,
    );
    if (refreshResult.data) {
      const tokens = refreshResult.data as {
        accessToken: string;
        refreshToken: string;
        user?: unknown;
      };
      setAccessToken(tokens.accessToken);
      setRefreshToken(tokens.refreshToken);
      try {
        const raw = localStorage.getItem(SESSION_STORAGE_KEY);
        const existing = raw ? JSON.parse(raw) : {};
        localStorage.setItem(
          SESSION_STORAGE_KEY,
          JSON.stringify({
            ...existing,
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
            user: tokens.user ?? existing.user,
          }),
        );
      } catch {
        // ignore storage failures
      }
      return true;
    }
    return false;
  })();
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
};

export const baseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await query(args, api, extraOptions);

  if (result.error?.status === 401 && refreshToken) {
    const refreshed = await attemptTokenRefresh(api, extraOptions);
    if (refreshed) {
      result = await query(args, api, extraOptions);
    } else {
      setAccessToken(undefined);
      setRefreshToken(undefined);
      try {
        localStorage.removeItem(SESSION_STORAGE_KEY);
      } catch {
        // ignore
      }
      const { store } = await import('./store');
      const { clearAdmin } = await import('./auth/auth.slice');
      store.dispatch(clearAdmin());
      if (
        typeof window !== 'undefined' &&
        window.location.pathname.startsWith('/admin') &&
        window.location.pathname !== '/admin'
      ) {
        window.location.href = '/admin';
      }
    }
  }

  return result;
};
